import React, { CSSProperties } from 'react';
import dayjs from 'dayjs';
import styled, { keyframes } from 'styled-components';
import { Col, Row } from '../../lib/utils';
import { colors } from '../../lib/colors';
import { Body3Bold, Body3Light, CaptionBold, CaptionRegular } from '../../lib/font';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const HoverItem = styled.div`
    width: 300px;
    min-height: 192px;
    z-index: 99;
    background-color: ${colors.WHITE_50};
    position: absolute;
    display: flex;
    flex-direction: column;
    border-radius: 16px;
    box-shadow: 4px 4px 8px 0px #0d41a675;
    padding: 14px 24px 14px 16px;
    animation: ${fadeIn} 1s ease forwards;
    cursor: default;
`;

const CLASS_TYPE_LABEL: Record<string, string> = {
    GROUP: '그룹 수업',
    PRIVATE_1ON2: '1:2 수업',
    PRIVATE_1ON3: '1:3 수업',
};

type Props = {
    /** 캘린더에 병합된 그룹 세션 아이템 (type === 'groupSession') */
    item: any;
    /** 블록 좌/우 어느 쪽에 띄울지 — 기존 PT hover 카드와 동일한 규칙 */
    style?: CSSProperties;
    accentColor: string;
};

/**
 * 기존 캘린더(일간/주간)의 그룹수업 블록 hover 카드.
 * PT 블록만 hover 상세가 있어 그룹수업은 클릭 전까지 정보를 알 수 없던 문제를 맞춘다.
 */
const GroupSessionHoverCard = ({ item, style, accentColor }: Props) => {
    const startTime = item?.startTime;
    const endTime = item?.endTime;
    const isOngoing = dayjs().isAfter(startTime) && dayjs().isBefore(endTime);
    const statusLabel = isOngoing ? '수업중' : dayjs().isAfter(endTime) ? '수업 완료' : '수업 예정';
    const classTypeLabel = CLASS_TYPE_LABEL[item?.classType] || CLASS_TYPE_LABEL.GROUP;
    const memberNames: string[] = Array.isArray(item?.memberNames) ? item.memberNames.filter(Boolean) : [];

    return (
        <HoverItem style={style}>
            <Row style={{ alignItems: 'center', justifyContent: 'space-between' }}>
                <Row style={{ alignItems: 'center', minWidth: 0, flex: 1, marginRight: 8 }}>
                    <Col
                        style={{
                            width: 8,
                            height: 22,
                            borderRadius: 8,
                            backgroundColor: accentColor,
                            marginRight: 11,
                            flexShrink: 0,
                        }}
                    />
                    <Body3Bold
                        style={{
                            color: colors.LAVEL_3,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >{`${item?.coachName || '강사 미정'} 강사`}</Body3Bold>
                </Row>
                <Row
                    style={{
                        height: 23,
                        padding: '0 12px',
                        borderRadius: 34,
                        border: `1px solid ${accentColor}`,
                        color: accentColor,
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}
                >
                    <CaptionBold>{statusLabel}</CaptionBold>
                </Row>
            </Row>

            <Body3Light style={{ color: colors.LAVEL_3, marginTop: 16 }}>
                {dayjs(startTime).format('M월 D일(ddd)')}
            </Body3Light>
            <Body3Bold style={{ color: colors.LAVEL_3, marginTop: 8 }}>
                {dayjs(startTime).format('A hh:mm ~ ')}
                {dayjs(endTime).format('A hh:mm ')}
                {`(${item?.duration}분)`}
            </Body3Bold>

            <Row style={{ alignItems: 'center', marginTop: 8, gap: 8 }}>
                <Body3Bold
                    style={{
                        color: colors.LAVEL_3,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        minWidth: 0,
                    }}
                >
                    {item?.title || '그룹 수업'}
                </Body3Bold>
                <span
                    style={{
                        fontSize: 11,
                        fontWeight: 700,
                        padding: '2px 8px',
                        borderRadius: 10,
                        backgroundColor: `${accentColor}20`,
                        color: accentColor,
                        flexShrink: 0,
                    }}
                >
                    {classTypeLabel}
                </span>
            </Row>

            <CaptionRegular style={{ color: colors.LAVEL_2, marginTop: 8 }}>
                예약 : {item?.currentCount ?? 0}/{item?.maxCapacity ?? '?'}명
                {item?.category ? ` · ${item.category}` : ''}
            </CaptionRegular>

            {memberNames.length > 0 && (
                <CaptionRegular style={{ color: colors.LAVEL_2, marginTop: 6 }}>
                    회원 : {memberNames.join(', ')}
                </CaptionRegular>
            )}

            {item?.memo && (
                <CaptionRegular
                    style={{
                        color: colors.LAVEL_2,
                        marginTop: 6,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                    }}
                >
                    메모 : {item.memo}
                </CaptionRegular>
            )}
        </HoverItem>
    );
};

export default GroupSessionHoverCard;
